/**
 * Booking flow state — Zustand store.
 * Ephemeral UI state for the multi-step booking wizard.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { InsuranceTier } from '../utils/constants'
import type { TripPriceBreakdown } from '../utils/pricing'
import { createPlatformStorage } from '../storage/zustand-storage'

const BOOKING_DRAFT_TTL_MS = 6 * 60 * 60 * 1000

interface BookingStoreState {
  draftUpdatedAt: number | null

  // Selection state
  vehicleId: string | null
  startDate: string | null // ISO 8601 date "YYYY-MM-DD"
  endDate: string | null // ISO 8601 date "YYYY-MM-DD"
  startTime: string | null // "HH:MM" 24h
  endTime: string | null // "HH:MM" 24h
  insuranceTier: InsuranceTier
  promoCode: string | null

  // Pickup/dropoff
  pickupAddress: string | null
  pickupLat: number | null
  pickupLng: number | null
  dropoffAddress: string | null
  dropoffLat: number | null
  dropoffLng: number | null
  isSameLocation: boolean

  // Computed pricing (set after calculation)
  pricing: TripPriceBreakdown | null

  // Actions
  setVehicle: (vehicleId: string) => void
  setDates: (startDate: string | null, endDate: string | null) => void
  // `null` es parte del contrato, igual que en setDates: una hora se puede
  // BORRAR. Hacia falta de verdad -- cambiar la fecha puede dejar la hora
  // elegida fuera de la ventana del carro, y sin poder pasar null no habia
  // forma de soltarla; se quedaba apuntando a un dia que ya no era el elegido.
  setStartTime: (time: string | null) => void
  setEndTime: (time: string | null) => void
  setInsuranceTier: (tier: InsuranceTier) => void
  setPromoCode: (code: string | null) => void
  setPickup: (address: string, lat: number, lng: number) => void
  setDropoff: (address: string, lat: number, lng: number) => void
  setSameLocation: (same: boolean) => void
  setPricing: (pricing: TripPriceBreakdown) => void
  reset: () => void
}

const initialState = {
  draftUpdatedAt: null,
  vehicleId: null,
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  insuranceTier: 'platform' as InsuranceTier,
  promoCode: null,
  pickupAddress: null,
  pickupLat: null,
  pickupLng: null,
  dropoffAddress: null,
  dropoffLat: null,
  dropoffLng: null,
  isSameLocation: true,
  pricing: null,
}

type PersistedBookingDraft = Pick<
  BookingStoreState,
  | 'draftUpdatedAt'
  | 'vehicleId'
  | 'startDate'
  | 'endDate'
  | 'startTime'
  | 'endTime'
  | 'insuranceTier'
  | 'promoCode'
  | 'pickupAddress'
  | 'pickupLat'
  | 'pickupLng'
  | 'dropoffAddress'
  | 'dropoffLat'
  | 'dropoffLng'
  | 'isSameLocation'
>

function touch<T extends Partial<BookingStoreState>>(patch: T): T & { draftUpdatedAt: number } {
  return { ...patch, draftUpdatedAt: Date.now() }
}

function isFreshDraft(state: BookingStoreState): boolean {
  return Boolean(state.draftUpdatedAt && Date.now() - state.draftUpdatedAt <= BOOKING_DRAFT_TTL_MS)
}

export const useBookingStore = create<BookingStoreState>()(
  persist(
    (set) => ({
      ...initialState,

      setVehicle: (vehicleId) =>
        set((state) => {
          if (state.vehicleId === vehicleId && isFreshDraft(state)) return touch({ vehicleId })
          return touch({ ...initialState, vehicleId })
        }),
      // UNA SOLA FORMA DE DECIR "sin fecha", y es `null`.
      //
      // El tipo del estado siempre dijo `string | null`, pero la firma de este
      // setter pedia `string`, asi que quien queria limpiar una fecha no tenia
      // como pasar null y mandaba `''`. Convivian dos vacios.
      //
      // No es cosmetico: la pantalla de reservar guarda con `{endDate && (...)}`,
      // y `''` no es falsy-y-desaparece en JSX, es un HIJO DE TEXTO. Se
      // disparaba con solo tocar el dia de recogida, en la ruta del dinero.
      //
      // QUE PASA DE VERDAD, medido y no supuesto --la primera version de este
      // comentario decia que "en nativo revienta la pantalla" y ESO ES FALSO
      // para esta app:
      //   - react-native-web: console.error "Unexpected text node: . A text node
      //     cannot be a child of a <View>", y un nodo de texto de mas en el DOM.
      //   - Fabric (arquitectura nueva, que es la nuestra: newArchEnabled true):
      //     `ReactFabric-dev.js` lo baja a console.error y `ReactFabric-prod.js`
      //     ni siquiera trae el mensaje. NO tumba nada.
      //   - Arquitectura VIEJA: `ReactNativeRenderer-prod.js` si lleva el
      //     invariante "Text strings must be rendered within a <Text> component",
      //     o sea ahi si es una caida en produccion.
      // Se arregla igual porque el codigo esta mal y la caida vuelve sola el dia
      // que algo corra sin Fabric, pero la gravedad hay que decirla como es.
      //
      // Se normaliza ACA y no en cada llamada: asi el que escriba `''` manana
      // tampoco puede volver a meter el defecto.
      setDates: (startDate, endDate) =>
        set(touch({ startDate: startDate || null, endDate: endDate || null })),
      setStartTime: (startTime) => set(touch({ startTime: startTime || null })),
      setEndTime: (endTime) => set(touch({ endTime: endTime || null })),
      setInsuranceTier: (insuranceTier) => set(touch({ insuranceTier })),
      setPromoCode: (promoCode) => set(touch({ promoCode })),
      setPickup: (address, lat, lng) =>
        set((state) =>
          touch({
            pickupAddress: address,
            pickupLat: lat,
            pickupLng: lng,
            ...(state.isSameLocation
              ? { dropoffAddress: address, dropoffLat: lat, dropoffLng: lng }
              : {}),
          }),
        ),
      setDropoff: (address, lat, lng) =>
        set(touch({ dropoffAddress: address, dropoffLat: lat, dropoffLng: lng })),
      setSameLocation: (isSameLocation) =>
        set((state) =>
          touch({
            isSameLocation,
            ...(isSameLocation &&
            state.pickupAddress &&
            state.pickupLat != null &&
            state.pickupLng != null
              ? {
                  dropoffAddress: state.pickupAddress,
                  dropoffLat: state.pickupLat,
                  dropoffLng: state.pickupLng,
                }
              : {}),
          }),
        ),
      setPricing: (pricing) => set({ pricing }),
      reset: () => set(initialState),
    }),
    {
      name: 'tago.booking.v1',
      storage: createPlatformStorage<PersistedBookingDraft>(),
      partialize: (state): PersistedBookingDraft => ({
        draftUpdatedAt: state.draftUpdatedAt,
        vehicleId: state.vehicleId,
        startDate: state.startDate,
        endDate: state.endDate,
        startTime: state.startTime,
        endTime: state.endTime,
        insuranceTier: state.insuranceTier,
        promoCode: state.promoCode,
        pickupAddress: state.pickupAddress,
        pickupLat: state.pickupLat,
        pickupLng: state.pickupLng,
        dropoffAddress: state.dropoffAddress,
        dropoffLat: state.dropoffLat,
        dropoffLng: state.dropoffLng,
        isSameLocation: state.isSameLocation,
      }),
      version: 1,
    },
  ),
)
